import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'TradeRadar';
const SITE_URL = 'https://traderadarglobal.com';

/**
 * Reusable SEO Head component for per-page meta tags.
 *
 * @param {string} title        – Page title (will be appended with " - TradeRadar" if not already)
 * @param {string} description  – Meta description (max ~155 chars recommended)
 * @param {string} path         – Canonical path, e.g. "/tariffs"
 * @param {string} type         – Open Graph type, default "website"
 * @param {string} keywords     – Comma-separated keywords
 * @param {object|array} jsonLd  – Optional JSON-LD structured data (single object or array)
 * @param {string} image        – OpenGraph/Twitter image URL
 * @param {string} imageAlt     – Alt text for the image
 * @param {number} imageWidth   – Image width in pixels (default 1200)
 * @param {number} imageHeight  – Image height in pixels (default 630)
 * @param {string} author       – Article author (for article type)
 * @param {string} publishedTime – Article published time (ISO 8601)
 * @param {string} modifiedTime  – Article modified time (ISO 8601)
 */
const SEOHead = ({
  title,
  description,
  path = '/',
  type = 'website',
  keywords = '',
  jsonLd = null,
  image = null,
  imageAlt = '',
  imageWidth = 1200,
  imageHeight = 630,
  author = 'TradeRadar',
  publishedTime = null,
  modifiedTime = null,
}) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} - ${SITE_NAME}`;
  const canonicalUrl = `${SITE_URL}${path}`;
  const resolveImageUrl = (src) => {
    if (!src) return `${SITE_URL}/og-image/default.png`;
    if (src.startsWith('http://') || src.startsWith('https://')) return src;
    if (src.startsWith('/')) return `${SITE_URL}${src}`;
    return `${SITE_URL}/${src}`;
  };
  const imageUrl = resolveImageUrl(image);
  const imageAltText = imageAlt || description.substring(0, 100);

  // Normalize jsonLd to array
  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      {/* Core */}
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={imageWidth.toString()} />
      <meta property="og:image:height" content={imageHeight.toString()} />
      <meta property="og:image:alt" content={imageAltText} />

      {/* Article-specific meta tags */}
      {type === 'article' && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAltText} />

      {/* JSON-LD Structured Data - Support multiple schemas */}
      {jsonLdArray.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export { SITE_URL, SITE_NAME };
export default SEOHead;


